import { DataTable, EmptyState, StatusBadge, type Spalte } from '../../muster';

/**
 * Der Bericht — Gewerkepaare, Geschoss/Quadrant, die schwersten
 * Durchdringungen und die Gegenproben, die die Zahlen gegen sich selbst
 * prüfen.
 *
 * Die drei `.slice(…)` des Bestands sind mit U6a entfallen: `DataTable`
 * virtualisiert ab 200 Zeilen und zeigt deshalb alle, ohne das Dokument
 * zuzustellen. Die „zehn schwersten" bleiben zehn — das ist eine FACHLICHE
 * Auswahl und kein Anzeigeschnitt.
 */
export function Bericht({ bericht }: { bericht: any }) {
  if (!bericht || !(bericht.gesamt > 0)) return null;

  const paarSpalten: Spalte<any>[] = [
    {
      schluessel: 'paar', titel: 'Gewerkepaar', immerSichtbar: true,
      wert: (p) => p.paar,
    },
    { schluessel: 'gesamt', titel: 'gesamt', art: 'zahl', wert: (p) => p.anzahl },
    {
      schluessel: 'echt', titel: 'echt', art: 'zahl', breite: '120px',
      wert: (p) => p.echt,
      inhalt: (p) => (p.echt > 0
        ? <StatusBadge zustand="fehler" text={`${p.echt}`} klein />
        : '0'),
    },
    {
      schluessel: 'tiefste', titel: 'tiefste', art: 'zahl', einheit: 'mm',
      nachkomma: 0,
      wert: (p) => p.groessteDurchdringungMm,
    },
  ];

  const quadrantSpalten: Spalte<any>[] = [
    {
      schluessel: 'geschoss', titel: 'Geschoss', immerSichtbar: true,
      wert: (g) => g.geschoss,
    },
    { schluessel: 'quadrant', titel: 'Quadrant', wert: (g) => g.quadrant },
    { schluessel: 'gesamt', titel: 'gesamt', art: 'zahl', wert: (g) => g.anzahl },
    {
      schluessel: 'vorgesehen', titel: 'vorgesehen', art: 'zahl',
      wert: (g) => g.vorgesehen,
    },
    { schluessel: 'gewollt', titel: 'gewollt', art: 'zahl', wert: (g) => g.gewollt },
    {
      schluessel: 'echt', titel: 'echt', art: 'zahl', breite: '110px',
      wert: (g) => g.echt,
      inhalt: (g) => (g.echt > 0
        ? <StatusBadge zustand="fehler" text={`${g.echt}`} klein />
        : '0'),
    },
    { schluessel: 'pruefen', titel: 'prüfen', art: 'zahl', wert: (g) => g.pruefen },
    {
      schluessel: 'anschluss', titel: 'Anschluss', art: 'zahl',
      wert: (g) => g.selbstberuehrung,
    },
  ];

  const schwersteSpalten: Spalte<any>[] = [
    {
      schluessel: 'gewerke', titel: 'Gewerke', immerSichtbar: true,
      wert: (g) => `${g.gewerkA} × ${g.gewerkB}`,
    },
    {
      schluessel: 'geschoss', titel: 'Geschoss',
      wert: (g) => g.geschoss ?? '',
      inhalt: (g) => g.geschoss ?? '—',
    },
    {
      schluessel: 'abschnitt', titel: 'Abschnitt',
      wert: (g) => g.planungsbereich ?? '',
      inhalt: (g) => g.planungsbereich ?? '—',
    },
    {
      schluessel: 'tiefe', titel: 'Tiefe', art: 'zahl', einheit: 'mm', nachkomma: 0,
      breite: '140px',
      wert: (g) => g.durchdringungMm,
      inhalt: (g) => (
        <StatusBadge
          klein zustand="fehler"
          text={`${Math.round(g.durchdringungMm ?? 0)} mm`}
        />
      ),
    },
    {
      schluessel: 'lage', titel: 'Lage', ausrichtung: 'rechts', sortierbar: false,
      wert: (g) => `${Math.round((g.mitteX ?? 0) / 1000)}/${
        Math.round((g.mitteY ?? 0) / 1000)}/${Math.round((g.mitteZ ?? 0) / 1000)}`,
      inhalt: (g) => (
        <span className="schwach" style={{ fontSize: 11 }}>
          {Math.round((g.mitteX ?? 0) / 1000)} /{' '}
          {Math.round((g.mitteY ?? 0) / 1000)} /{' '}
          {Math.round((g.mitteZ ?? 0) / 1000)} m
        </span>
      ),
    },
  ];

  return (
    <>
      <DataTable
        name={`Bericht — ${bericht.echt} echte von ${bericht.gesamt}`}
        spalten={paarSpalten}
        zeilen={(bericht.jeGewerkepaar ?? []) as any[]}
        schluessel={(p: any) => p.paar}
        sortierung={{ spalte: 'echt', richtung: 'ab' }}
        ausfuhr
        ausfuhrDateiname="Bericht-Gewerkepaare"
        leer={<EmptyState titel="Kein Gewerkepaar im Bericht." />}
      />

      {/* --- Je Geschoss und Quadrant ------------------------------
          Das ist die Gliederung, in der GEARBEITET wird: Ein Planer
          bekommt einen Quadranten in einem Geschoss und nicht 'alle
          Kollisionen zwischen Lüftung und Heizung'. Die Gewerkesicht
          sagt, WAS schiefgeht — diese sagt, WER es richtet. */}
      {(bericht.jeGeschossQuadrant ?? []).length > 0 && (
        <DataTable
          name="Je Geschoss und Quadrant"
          spalten={quadrantSpalten}
          zeilen={bericht.jeGeschossQuadrant as any[]}
          schluessel={(g: any) => `${g.geschoss}|${g.quadrant}`}
          sortierung={{ spalte: 'echt', richtung: 'ab' }}
          hoehe={380}
          ausfuhr
          ausfuhrDateiname="Bericht-Geschoss-Quadrant"
        />
      )}

      {/* --- Die schwersten ---------------------------------------------
          Nach Durchdringungstiefe, aber NUR die echten. Eine
          Selbstberührung an einer Muffe kann 200 mm tief sein und ist
          trotzdem nichts — wer ohne die Einstufung sortiert, bekommt
          eine Liste von Anschlüssen. */}
      {(bericht.groesste ?? []).length > 0 && (
        <DataTable
          name="Die zehn schwersten"
          beschreibung="Nach Durchdringungstiefe, nur echte."
          spalten={schwersteSpalten}
          zeilen={(bericht.groesste as any[]).slice(0, 10)}
          schluessel={(g: any) => g.id}
          suche={false}
          sortierung={{ spalte: 'tiefe', richtung: 'ab' }}
        />
      )}

      {(bericht.gegenproben ?? []).length > 0 && (
        <div style={{ marginTop: 14 }}>
          {(bericht.gegenproben ?? []).map((g: string, i: number) => (
            <div key={i} className="hinweis" style={{ marginBottom: 6 }}>{g}</div>
          ))}
        </div>
      )}
    </>
  );
}
